import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { diagnostic, contactInfo } from "../data/mock";
import { Button } from "./ui/button";
import { ArrowRight, Check, Clock } from "lucide-react";
import { Reveal, ChapterTag, EASE } from "../lib/motion";

export const DiagnosticSection = () => {
  const navigate = useNavigate();
  return (
    <section id="diagnostic" data-testid="diagnostic-section" className="py-24 sm:py-32 bg-[#032e2d] relative grain overflow-hidden">
      <div className="absolute top-0 right-0 w-[480px] h-[480px] rounded-full bg-brand-500/10 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-12 items-center">
          <Reveal>
            <ChapterTag num="03" label="The Diagnostic" dark />
            <h2 className="mt-6 font-display font-semibold text-white tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-3xl leading-[1.1]">
              {diagnostic.heading}
            </h2>
            <p className="mt-4 font-display text-lg md:text-xl text-brand-300">
              {diagnostic.subheading}
            </p>
            <p className="mt-6 text-teal-100/75 leading-relaxed max-w-3xl text-base">
              {diagnostic.copy}
            </p>
          </Reveal>

          {/* Sample snapshot teaser */}
          <Reveal delay={0.15}>
            <button
              onClick={() => navigate("/diagnostic")}
              data-testid="diagnostic-teaser-card"
              className="group w-full text-left rounded-3xl bg-teal-50 p-6 sm:p-7 shadow-[0_20px_60px_rgba(0,0,0,0.25)] hover:-translate-y-1.5 transition-all duration-300 relative overflow-hidden"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">Sample snapshot</span>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#032e2d] bg-brand-100 px-3 py-1 rounded-full">
                  <Clock className="w-3 h-3" />
                  Takes 5–7 minutes
                </span>
              </div>
              <div className="space-y-3.5">
                {[
                  { label: "Growth & Commercial", val: 4.2, rag: "Current strength", bar: "bg-emerald-500", chip: "text-emerald-700" },
                  { label: "Customer", val: 3.1, rag: "Improvement opportunity", bar: "bg-amber-400", chip: "text-amber-700" },
                  { label: "Operations", val: 2.2, rag: "Potential constraint", bar: "bg-red-400", chip: "text-red-700" },
                  { label: "Technology, Data & AI", val: 2.9, rag: "Improvement opportunity", bar: "bg-amber-400", chip: "text-amber-700" },
                ].map((d) => (
                  <div key={d.label}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-stone-800">{d.label}</span>
                      <span className={`text-[10px] font-semibold ${d.chip}`}>{d.val.toFixed(1)} · {d.rag}</span>
                    </div>
                    <div className="h-2 rounded-full bg-stone-200/70 overflow-hidden">
                      <div className={`h-full rounded-full ${d.bar}`} style={{ width: `${(d.val / 5) * 100}%` }} />
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-stone-400 pt-0.5">+ 3 more dimensions, patterns and a downloadable PDF</p>
              </div>
              <div className="mt-5 pt-4 border-t border-stone-200 flex items-center justify-between">
                <span className="text-sm font-semibold text-[#032e2d]">21 quick statements · Instant results</span>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 group-hover:gap-2.5 transition-all duration-300">
                  Try it
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </button>
          </Reveal>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {diagnostic.areas.map((area, i) => {
            const Icon = area.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.09, ease: EASE }}
                className="rounded-2xl border border-teal-100/15 bg-teal-50/[0.04] backdrop-blur-sm p-7 hover:border-brand-400/40 hover:bg-teal-50/[0.07] transition-all duration-300"
                data-testid={`diagnostic-area-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-brand-500/15 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-brand-300" />
                </div>
                <h3 className="text-base md:text-lg font-display font-semibold text-white mb-3">{area.title}</h3>
                <ul className="space-y-2">
                  {area.items.map((item, j) => (
                    <li key={j} className="text-sm text-teal-100/65 leading-relaxed flex items-start gap-2">
                      <span className="text-brand-400 mt-1.5 w-1 h-1 rounded-full bg-brand-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Output roadmap */}
        <Reveal delay={0.1} className="mt-14 rounded-3xl bg-teal-50 p-8 sm:p-12" data-testid="diagnostic-output">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <h3 className="font-display font-semibold text-[#032e2d] text-2xl sm:text-3xl leading-tight">
                {diagnostic.outputHeading}
              </h3>
              <p className="mt-3 text-stone-600">{diagnostic.outputIntro}</p>
              <p className="mt-6 text-sm text-stone-500  leading-relaxed border-l-2 border-[#A86532] pl-4">
                {diagnostic.note}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  onClick={() => navigate("/diagnostic")}
                  data-testid="diagnostic-start-link"
                  className="bg-[#032e2d] hover:bg-brand-700 text-teal-50 rounded-full h-12 px-7 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5"
                >
                  Start the Growth & Efficiency Diagnostic
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  onClick={() => window.open(contactInfo.bookingUrl, "_blank", "noopener,noreferrer")}
                  data-testid="diagnostic-cta"
                  variant="outline"
                  className="rounded-full h-12 px-7 text-sm font-medium border-stone-300 text-stone-800 hover:bg-stone-100"
                >
                  {diagnostic.cta}
                </Button>
              </div>
            </div>
            <ul className="grid sm:grid-cols-2 gap-3">
              {diagnostic.outputs.map((out, i) => (
                <li key={i} className="flex items-start gap-2.5 bg-white rounded-xl px-4 py-3 border border-stone-100">
                  <Check className="w-4 h-4 mt-0.5 text-brand-600 flex-shrink-0" />
                  <span className="text-sm text-stone-700">{out}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
